import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Camera, Upload, Trash2, X, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const MAX = 3;

function Thumb({ att, onClick }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true; let objUrl = null;
    (async () => {
      try {
        const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
        if (!active) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch { if (active) setErr(true); }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [att.id]);
  if (err) return <div className="h-20 w-20 rounded-md bg-paper-alt flex items-center justify-center text-muted"><ImageOff size={18} /></div>;
  if (!url) return <div className="h-20 w-20 rounded-md bg-paper-alt flex items-center justify-center"><Loader2 size={16} className="animate-spin text-muted" /></div>;
  return <img src={url} alt={att.original_filename} onClick={() => onClick(url)} className="h-20 w-20 rounded-md object-cover border border-line cursor-pointer" data-testid={`attach-thumb-${att.id}`} />;
}

export function AttachmentManager({ txnId, docNo, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!txnId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions/${txnId}/attachments`);
      setList(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [txnId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (list.length + files.length > MAX) { toast.error(`Max ${MAX} photos per voucher`); return; }
    const fd = new FormData();
    files.forEach((f) => fd.append("files[]", f));
    setUploading(true);
    try {
      await api.post(`/transactions/${txnId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} photo(s) attached`);
      await refresh(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setUploading(false); }
  };

  const del = async (aid) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/attachments/${aid}`);
      toast.success("Attachment deleted");
      await refresh(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const atMax = list.length >= MAX;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="attachment-manager">
        <DialogHeader>
          <DialogTitle>Voucher Photos {docNo ? `· ${docNo}` : ""}</DialogTitle>
          <DialogDescription>Attach up to {MAX} bill/voucher photos (images, ≤5 MB each).</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 min-h-[5rem]">
            {loading ? (
              <div className="text-sm text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : list.length === 0 ? (
              <div className="text-sm text-muted">No photos attached yet.</div>
            ) : list.map((a) => (
              <div key={a.id} className="relative group">
                <Thumb att={a} onClick={(u) => setLightbox(u)} />
                {isAdmin && (
                  <button onClick={() => del(a.id)} data-testid={`attach-del-${a.id}`}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-cashout text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple
            onChange={onPick} className="hidden" data-testid="attach-file-input" />

          <div className="flex gap-2">
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || atMax}
              data-testid="attach-capture-btn" className="bg-ink hover:bg-ink-soft text-white">
              {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Camera size={14} className="mr-1.5" />}
              {atMax ? "Limit reached" : "Add / Capture Photo"}
            </Button>
            <span className="text-xs text-muted self-center font-mono">{list.length}/{MAX}</span>
          </div>
        </div>

        {lightbox && (
          <div onClick={() => setLightbox(null)} data-testid="attach-lightbox"
            className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out">
            <button className="absolute top-4 right-4 text-white"><X size={28} /></button>
            <img src={lightbox} alt="voucher" className="max-h-[85vh] max-w-full rounded-md" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
